import axios from 'axios';
import type { Repository, Chart, ChartVersion, ChartDetails, Session, RenderResult, SecurityScanResult } from '../types';

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
});

// Repository APIs
export const repoApi = {
  list: async (): Promise<Repository[]> => {
    const { data } = await api.get('/repos');
    return data;
  },

  add: async (name: string, url: string): Promise<void> => {
    await api.post('/repos', { name, url });
  },

  remove: async (name: string): Promise<void> => {
    await api.delete(`/repos/${name}`);
  },

  update: async (name: string): Promise<void> => {
    await api.post(`/repos/${name}/update`);
  },
};

// Chart APIs
export const chartApi = {
  list: async (repo?: string): Promise<Chart[] | Record<string, Chart[]>> => {
    const { data } = await api.get('/charts', { params: { repo } });
    return data;
  },

  get: async (repo: string, chart: string, version?: string): Promise<ChartDetails> => {
    const { data } = await api.get(`/charts/${repo}/${chart}`, {
      params: version ? { version } : undefined,
    });
    return data;
  },

  getVersions: async (repo: string, chart: string): Promise<ChartVersion[]> => {
    const { data } = await api.get(`/charts/${repo}/${chart}/versions`);
    return data;
  },

  getDefaultValues: async (repo: string, chart: string, version?: string): Promise<{ values: string }> => {
    const { data } = await api.get(`/charts/${repo}/${chart}/values`, {
      params: version ? { version } : undefined,
    });
    return data;
  },

};

// Session APIs
export const sessionApi = {
  create: async (repository: string, chartName: string, version?: string): Promise<{
    session: Session;
    metadata: ChartDetails['metadata'];
    templates: Record<string, string>;
    helpers: Record<string, string>;
  }> => {
    const { data } = await api.post('/sessions', { repository, chartName, version });
    return data;
  },

  get: async (id: string): Promise<Session> => {
    const { data } = await api.get(`/sessions/${id}`);
    return data;
  },

  update: async (id: string, values?: string, templates?: Record<string, string>): Promise<Session> => {
    const { data } = await api.put(`/sessions/${id}`, { values, templates });
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/sessions/${id}`);
  },

  getVersions: async (id: string): Promise<ChartVersion[]> => {
    const { data } = await api.get(`/sessions/${id}/versions`);
    return data;
  },

  changeVersion: async (id: string, version: string): Promise<{
    session: Session;
    metadata: ChartDetails['metadata'];
    templates: Record<string, string>;
    helpers: Record<string, string>;
  }> => {
    const { data } = await api.post(`/sessions/${id}/version`, { version });
    return data;
  },
};

// Render APIs
export const renderApi = {
  render: async (
    sessionId: string,
    values?: string,
    templates?: Record<string, string>,
    releaseName?: string,
    namespace?: string
  ): Promise<RenderResult> => {
    const { data } = await api.post('/render', { sessionId, values, templates, releaseName, namespace });
    return data;
  },

  validate: async (values: string): Promise<{ valid: boolean; errors?: { message: string }[] }> => {
    const { data } = await api.post('/render/validate', { values });
    return data;
  },
};

// Security APIs
export const securityApi = {
  scan: async (manifests: Record<string, string>): Promise<SecurityScanResult> => {
    const { data } = await api.post('/security/scan', { manifests });
    return data;
  },

  scanSession: async (
    sessionId: string,
    values?: string,
    templates?: Record<string, string>,
    releaseName?: string,
    namespace?: string
  ): Promise<SecurityScanResult> => {
    const { data } = await api.post('/security/scan-session', {
      sessionId,
      values,
      templates,
      releaseName,
      namespace,
    });
    return data;
  },
};

export default api;
